import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../../classes/util';
import { environment } from '../../../environment';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter, JSONConverter } from '../../../inputConverter';
import { FillStyleOptions, LineStyleOptions } from '../../../selectionOptions';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

const RadiusOffsetDivisor = 3.2;
const initilizeDebounceTime = 50;

let Draw: __esri.DrawConstructor;
let Graphic: __esri.GraphicConstructor;
let GeometryService: __esri.GeometryServiceConstructor;
let AreasAndLengthsParameters: __esri.AreasAndLengthsParametersConstructor;
let Polygon: __esri.PolygonConstructor;
let Circle: __esri.CircleConstructor;
let GeodesicUtils: __esri.geodesicUtils;
let WebMercatorUtils: __esri.webMercatorUtils;
let GeometryEngine: __esri.geometryEngine;
let Point: __esri.PointConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-draw-polygon',
    templateUrl: './draw-polygon.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './draw-polygon.component.scss']
})
export class DrawPolygonComponent extends BaseComponent implements OnInit {
    @Output() readonly drawComplete: EventEmitter<__esri.Graphic> = new EventEmitter();
    @Output() readonly drawLayer: EventEmitter<__esri.GraphicsLayer> = new EventEmitter();
    @Input()
    set enabled(enabled: boolean) {
        this._enabled = enabled;
        if (this._enabled) {
            this.setupHandleHiddenLayer();
            this.createPolygonDrawAction();
        } else {
            this.draw.reset();
            this.teardownHandleHiddenLayer();
            if (this.graphic) {
                this.mapView.graphics.remove(this.graphic);
            }
            this.cdr.detectChanges();
        }
    }
    _enabled: boolean;

    @Input()
    symbolProperties: any;
    createLayerForEachGraphic: boolean;

    mapView: NonNullable<__esri.MapView>;
    draw: NonNullable<__esri.Draw>;
    graphic: NonNullable<__esri.Graphic>;
    graphicsLayer: NonNullable<__esri.GraphicsLayer>;
    isLoading: boolean;

    @Input()
    showSettings = {
        createLayerForEachGraphic: true,
        type: true,
        mode: true,
        fillStyle: true,
        fillColor: true,
        outlineStyle: true,
        outlineColor: true,
        outlineWidth: true,
        areaUnit: true
    };

    @Input()
    @InputConverter(JSONConverter)
    defaultSymbolProperties = {
        type: 'simple-fill',
        color: 'purple',
        style: 'solid',
        outline: {
            color: 'white',
            style: 'solid',
            width: 1
        }
    };

    fillStyleOptions = FillStyleOptions;
    outlineStyleOptions = LineStyleOptions;

    polygonTypes: { name: string; value: PolygonType; hybridable?: boolean }[] = [
        { value: 'polygon', name: 'Polygon', hybridable: true },
        { value: 'rectangle', name: 'Rectangle' },
        { value: 'circle', name: 'Circle' },
        { value: 'rectangle', name: 'Arrow' }
    ];
    selectedPolygonType = this.polygonTypes[0];

    polygonModes: { name: string; value: PolygonMode; description: string; disabled?: boolean }[] = [
        { value: 'click', name: 'Click', description: 'Vertices are added when the pointer is clicked.' },
        { value: 'freehand', name: 'Freehand', description: 'Vertices are added while the pointer is dragged.' },
        { value: 'hybrid', name: 'Hybrid', description: 'Vertices are added while the pointer is clicked or dragged.' }
    ];
    selectedPolygonMode = this.polygonModes[0];

    @Input()
    readonly areaUnits: { name: string; value: AreaUnit }[] = [
        { name: 'None', value: null },
        { name: 'Acres', value: 'acres' },
        { name: 'Square Feet', value: 'square-feet' },
        { name: 'Square Miles', value: 'square-miles' },
        { name: 'Square Meters', value: 'square-meters' },
        { name: 'Square Kilometers', value: 'square-kilometers' }
    ];

    @Input()
    selectedAreaUnit = this.areaUnits[0];

    geometryService: NonNullable<__esri.GeometryService>;

    constructor(
        _vcr: ViewContainerRef,
        private readonly cdr: ChangeDetectorRef,
        private readonly layerService: LayerService
    ) {
        super(_vcr);
    }

    colorPicker: ViewContainerRef;
    @ViewChild('colorPicker') set colorPickerRef(el: ViewContainerRef) { this.colorPicker = el; }
    outlineColorPicker: ViewContainerRef;
    @ViewChild('outlineColorPicker') set outlineColorPickerRef(el: ViewContainerRef) { this.outlineColorPicker = el; }

    async ngOnInit() {
        try {
            this.createLayerForEachGraphic = true;
            this.symbolProperties = this.defaultSymbolProperties;
            this.mapView = await this.setupMapViewRef(this.mapView);
            [
                Draw,
                Graphic,
                GeometryService,
                AreasAndLengthsParameters,
                Polygon,
                Circle,
                GeodesicUtils,
                WebMercatorUtils,
                GeometryEngine,
                Point
            ] = await Promise.all([
                ESRI.Draw,
                ESRI.Graphic,
                ESRI.GeometryService,
                ESRI.AreasAndLengthsParameters,
                ESRI.Polygon,
                ESRI.Circle,
                ESRI.GeodesicUtils,
                ESRI.WebMercatorUtils,
                ESRI.GeometryEngine,
                ESRI.Point
            ]);
            this.draw = new Draw({ view: this.mapView });
        } catch (err) {
            this.readyProblem(err);
        } finally {
            this.isReady();
        }
    }

    createPolygonDrawAction() {
        const action = this.draw.create(this.selectedPolygonType.value, { mode: this.selectedPolygonMode.value });
        action.on(['vertex-add', 'vertex-remove', 'cursor-update'], (evt: IUpdateEvent) => {
            this.graphic = this.createPolygonGraphic(evt.vertices);
            this.mapView.graphics.add(this.graphic);
        });

        action.on('draw-complete', (evt: IDrawCompleteEvent) => {
            const completedGraphic = this.createPolygonGraphic(evt.vertices);
            this.addPolygonGraphic(completedGraphic);
            this.drawComplete.emit(completedGraphic);
            this.reset();
        });
    }

    private createPolygonGraphic(vertices: number[][]) {
        if (vertices.length > 1 && this.graphic) {
            this.mapView.graphics.remove(this.graphic);
        }

        let polygon = new Polygon({ spatialReference: this.mapView.spatialReference, rings: [vertices] });

        const nonPolygonVerticiesLength = 2;
        if (vertices.length === nonPolygonVerticiesLength) {
            const [startX, startY] = vertices[0];
            const [endX, endY] = vertices[1];
            const length = this.getLength(polygon);
            switch (this.selectedPolygonType.value) {
                case 'rectangle':
                    if (this.selectedPolygonType.name === 'Arrow') {
                        polygon.rings = this.createArrowVerticies(startX, startY, endX, endY, length);
                    } else {
                        polygon.rings = [[
                            [startX, startY],
                            [endX, startY],
                            [endX, endY],
                            [startX, endY],
                            [startX, startY]
                        ]];
                    }
                    break;
                case 'circle':
                    polygon = new Circle({
                        spatialReference: this.mapView.spatialReference,
                        center: { x: startX, y: startY, spatialReference: this.mapView.spatialReference },
                        radius: length
                    });
                    break;
                default:
                    break;
            }
        }

        const graphic = new Graphic({
            geometry: polygon,
            symbol: this.symbolProperties
        });

        return graphic;
    }

    private getLength(polygon: __esri.Polygon) {
        length = GeodesicUtils.geodesicLengths([(WebMercatorUtils.webMercatorToGeographic(polygon) as __esri.Polygon)])[0];
        return length + (length / RadiusOffsetDivisor);
    }

    private createArrowVerticies(startX: number, startY: number, endX: number, endY: number, length: number) {
        const radsMultiplyer = 2;
        const halfDegrees = 180;
        const widthDivisor = 0.2;
        const halfPositionDivisor = 2;

        const end = new Point({ x: startX, y: startY, spatialReference: this.mapView.spatialReference });
        const start = new Point({ x: endX, y: endY, spatialReference: this.mapView.spatialReference });
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        let rads = Math.atan2(dx, dy);

        if (rads < 0) {
            rads = Math.abs(rads);
        } else {
            rads = radsMultiplyer * Math.PI - rads;
        }

        const degrees = rads * halfDegrees / Math.PI;

        const width = widthDivisor * length;

        const bottomLeft = [start.x - width / halfPositionDivisor, start.y];
        const bottomRight = [start.x + width / halfPositionDivisor, start.y];
        const midLeft: [number, number[]] = [bottomLeft[0], [bottomLeft[1] + length - width]];
        const midRight: [number, number[]] = [bottomRight[0], [bottomRight[1] + length - width]];
        const edgeLeft: [number, number[]] = [midLeft[0] - width / halfPositionDivisor, midLeft[1]];
        const edgeRight: [number, number[]] = [midRight[0] + width / halfPositionDivisor, midRight[1]];
        const point = [start.x, start.y + length];

        const poly = new Polygon({
            rings: [
                [
                    point,
                    edgeLeft as any,
                    midLeft as any,
                    bottomLeft,
                    bottomRight,
                    midRight as any,
                    edgeRight as any,
                    point
                ]
            ],
            spatialReference: this.mapView.spatialReference
        });

        const rotatedPoly = GeometryEngine.rotate(poly, degrees, start) as __esri.Polygon;

        return rotatedPoly.rings;
    }

    private async addPolygonGraphic(graphic: __esri.Graphic) {
        if (!this.graphicsLayer || this.createLayerForEachGraphic) {

            let title = this.selectedPolygonType.name;
            if (this.selectedPolygonMode.value === 'freehand') {
                title = `${this.selectedPolygonMode.name} ${title}`;
            }
            if (this.selectedAreaUnit.value) {
                this.isLoading = true;
                if (!this.geometryService) {
                    this.geometryService = new GeometryService({ url: environment.geometryServiceUrl });
                }
                const result: { areas: number[]; lengths: number[] } = await this.geometryService.areasAndLengths(new AreasAndLengthsParameters({
                    areaUnit: this.selectedAreaUnit.value,
                    calculationType: 'preserve-shape',
                    lengthUnit: 'meters',
                    polygons: [graphic.geometry]
                }));
                if (result && result.areas.length) {
                    title += ` - ${result.areas[0]} ${this.selectedAreaUnit.name}`;
                }
                this.isLoading = false;
                this.cdr.detectChanges();
            }

            this.graphicsLayer = await this.layerService.addGraphicsLayer({ title, graphicLayer: 'polygon' });
        }

        this.graphicsLayer.add(graphic);
        this.drawLayer.emit(this.graphicsLayer);
        this.graphic = null;
    }

    reset() {
        this.draw.reset();
        this.polygonModes.find(mode => mode.value === 'hybrid').disabled = !this.selectedPolygonType.hybridable;
        if (this.selectedPolygonMode.value === 'hybrid' && !this.selectedPolygonType.hybridable) {
            this.selectedPolygonMode = this.polygonModes[0];
        }
        this.createPolygonDrawAction();
    }

    initializing = false;
    private readonly listners: IHandle[] = [];
    private setupHandleHiddenLayer() {
        this.initializing = true;
        const stopInitialCheck = util.debounce(() => { this.initializing = false; }, initilizeDebounceTime);
        this.listners.push(this.layerService.mapRef.allLayers.on('before-add', (event) => {
            if (this.initializing && event.item.listMode === 'hide') {
                event.item.set('hideInLegend', true);
                event.item.set('drawPolygonToolGeneratedLayer', true);
                stopInitialCheck();
            }
        }));
        this.listners.push(this.layerService.mapRef.allLayers.on('after-remove', (event) => {
            if (event.item.get<boolean>('drawPolygonToolGeneratedLayer')) {
                this.initializing = true;
            }
        }));
    }
    private teardownHandleHiddenLayer() {
        while (this.listners.length) {
            this.listners.pop().remove();
        }
    }
}

type PolygonType = 'polygon' | 'rectangle' | 'circle';
type PolygonMode = 'hybrid' | 'freehand' | 'click';
type AreaUnit = 'acres' | 'hectares' | 'square-miles' | 'square-kilometers' | 'square-meters' | 'square-feet' | 'square-yards';

interface IDrawCompleteEvent {
    vertices: number[][];
    preventDefault: () => void;
    defaultPrevented: boolean;
    type: 'draw-complete';
}

interface IUpdateEvent {
    vertices: number[][];
    vertexIndex: number;
    preventDefault: () => void;
    defaultPrevented: boolean;
    type: 'cursor-update' | 'vertex-add' | 'vertex-remove';
}
